import React, { useState, useEffect, Suspense, useCallback } from 'react';

import { PriceGroup, RawHtml, SizeSelector, Text, Usp } from '$shared/components';
import { Layout, ImageDisplay, AccordionInfo, ColorSelector, ProductActions } from '../';
import {
    StyledList,
    StyledAccordionContainer,
    StyledSizeActions,
    StyledSizeGuideWrapper,
} from './styled';

import { queries, Spacer } from '~/theme';
import { useTranslation } from '~/shared/utils/translation';
import { useNotification } from '~/shared/hooks/useNotification';
import { ProductViewModel } from '~/lib/data-contract';

import { useBasketAdd } from '~/features/basket';
import { useProduct } from '~/shared/utils/product/hooks/useProduct';
import { useFrame } from '~/shared/utils';
import { useAlgoliaAnalytics } from '~/shared/hooks';
import qs from 'qs';
import { Overlay } from '~/shared/components';
import { useMedia } from 'react-use';
import { M130Props, M131Props } from '../..';
import { StyledProductImageColumn, StyledProductInfoColumn } from '../Layout/styled';
import dynamic from 'next/dynamic';
import { ProductTags } from '~/shared/components/ProductTags';
import { Easysize } from '../Easysize';
import { addResizeVariablesToProductImage } from '$shared/utils/product';
import Head from 'next/head';

const ReserveContainer = dynamic(() => import('~/features/Reserve/ReserveContainer'), {
    suspense: true,
});

export const ProductDetail = ({ sizeGuideContent }: M130Props | M131Props) => {
    const { translate } = useTranslation();
    const { trackAddToBasket } = useAlgoliaAnalytics();
    const { data: frame } = useFrame();
    const { push } = useNotification();
    const { add } = useBasketAdd();
    const { data: product } = useProduct();
    const isMobile = !useMedia(queries.sm, false);

    const [variantId, setVariantId] = useState<string | null>(null);
    const [animateSize, setAnimateSize] = useState(false);
    const [isReserveActive, setIsReserveActive] = useState(false);
    const [showSizeErrorMessage, setShowSizeErrorMessage] = useState(false);

    const {
        images,
        accordions,
        name,
        subtitle,
        priceDetails,
        colors,
        sizes,
        productColorId: productModelColorId,
        imageTags,
        sizingText,
    } = product as ProductViewModel;

    const onReserveDismiss = useCallback(() => setIsReserveActive(false), [setIsReserveActive]);

    if (sizes?.length === 1 && !variantId) {
        setVariantId(sizes[0].variantId || null);
    }

    const onSizeSelected = useCallback((variantId: string) => {
        setVariantId(variantId);
        setShowSizeErrorMessage(false);
    }, []);

    const addToBasket = useCallback(() => {
        if (!variantId) {
            setAnimateSize(true);
            setShowSizeErrorMessage(true);
            return;
        }

        if (!productModelColorId) {
            push({ severity: 'warning', text: translate('message.fatalError') });
            return;
        }

        const { hash } = window.location;
        const params = qs.parse(hash.slice(1));
        if (params.queryId) {
            trackAddToBasket([productModelColorId.toString()], params.queryId.toString());
        }
        add({
            productId: productModelColorId,
            variantId,
            quantity: 1,
            pageViewId: window.EasySizeParametersDebug?.easysize?.pageview_id || '',
            userId: window.EasySizeParametersDebug?.easysize?.user_id || '',
        });
    }, [add, productModelColorId, push, trackAddToBasket, translate, variantId]);

    useEffect(() => {
        // Reset variant id on product change and track new product
        setVariantId(null);
    }, [product?.productColorId]);

    if (!product) {
        return <></>;
    }

    // We want to make sure the og:image is exactly the same as presented in the Structured Data, so here we go!
    const resizedFirstProductImage = addResizeVariablesToProductImage(images?.[0]?.url);

    return (
        <>
            {resizedFirstProductImage && (
                <Head>
                    <meta name="image" content={resizedFirstProductImage} />
                    <meta property="og:image" content={resizedFirstProductImage} />
                </Head>
            )}
            <Spacer sm="2" md="6" />
            <Layout>
                <StyledProductImageColumn>
                    <ImageDisplay images={images} alt={`${name} ${product?.productId}`} />
                </StyledProductImageColumn>
                <StyledProductInfoColumn>
                    <div>
                        {/* TODO: PDP Breadcrumb */}
                        {name && <Text as="h1" variant="display2" children={name} />}
                        {subtitle && <Text children={subtitle} />}
                        <ProductTags
                            productTags={imageTags}
                            layout="spaced-list"
                            selectFrom="bottom"
                            spacing={['2', '3']}
                        />
                        <Spacer xs="2" md="3" />
                        {priceDetails && (
                            <PriceGroup
                                {...priceDetails}
                                variant="display4"
                                imageTags={imageTags}
                                isPDP
                            />
                        )}
                        <Spacer xs="2" md="4" />
                        <ColorSelector colors={colors} activeColorId={productModelColorId} />
                        <Spacer xs="2" md="4" />
                        <SizeSelector
                            name="pdp-sizes"
                            sizes={sizes}
                            activeSizeId={variantId}
                            onSelect={onSizeSelected}
                            onAnimationEnd={() => setAnimateSize(false)}
                            isAnimating={animateSize}
                            showErrorMessage={showSizeErrorMessage}
                            sizingText={sizingText}
                        />
                        <Spacer xs="2" md="3" />
                        <StyledSizeActions>
                            {sizeGuideContent && (
                                <StyledSizeGuideWrapper>
                                    <RawHtml html={sizeGuideContent} />
                                </StyledSizeGuideWrapper>
                            )}
                            <Easysize
                                product={product}
                                activeSizeId={variantId}
                                onSizeFound={onSizeSelected}
                            />
                        </StyledSizeActions>
                        {showSizeErrorMessage ? <Spacer xs="5" sm="7" /> : <Spacer xs="2" sm="5" />}
                        <ProductActions
                            sizes={sizes}
                            activeSizeId={variantId}
                            productColorId={productModelColorId}
                            onAddToBasket={addToBasket}
                            onReserve={() => setIsReserveActive(true)}
                        />
                        <Spacer xs="2" md="4" />
                        {frame?.usp?.uspList?.length ? (
                            <StyledList>
                                {frame.usp.uspList?.map((uspItem, index) => (
                                    <li key={index}>
                                        <Usp
                                            uspItem={uspItem}
                                            shade="paragraph"
                                            textDecoration="underline"
                                        />
                                    </li>
                                ))}
                            </StyledList>
                        ) : null}
                        <Spacer xs="2" md="6" />

                        <StyledAccordionContainer>
                            <AccordionInfo accordions={accordions} />
                        </StyledAccordionContainer>
                    </div>
                </StyledProductInfoColumn>
            </Layout>
            <Suspense>
                <Overlay
                    onDismiss={onReserveDismiss}
                    open={isReserveActive}
                    variant={isMobile ? 'full' : 'minimal'}
                    scrollLock={true}
                    headline={translate('reservation.overlayHeading')}
                >
                    <ReserveContainer
                        initialVariantId={variantId}
                        product={product}
                        setIsReserveActive={setIsReserveActive}
                    />
                </Overlay>
            </Suspense>
        </>
    );
};
